import { useCallback, useEffect, useState } from 'react';
import {
  useForm,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import {
  ajvResolver,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
  usePrevious,
  ValidatorsProps,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import isEqual from 'lodash.isequal';
import {
  AffilatedCompanyFormValues,
  AllowedCategory,
  ApplicationAgreement,
  ApplicationDocument,
  CRSAndFatcaFormValues,
  FormValues,
  NewApplicationIndividualStepsStatuses,
  NewApplicationListItem,
  NewApplicationStatus,
  NewApplicationsValidatorResponse,
} from '../../types';
import { updateApplicationStepStatuses } from '../helpers/update-appliaction-step-status';
import { OngoingCheckingStatus, OngoingMonitring } from '../../../common/types';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import {
  useLazyGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersEmployeesListQuery,
} from '../../../../store/dictionaries/services';
import {
  useLazyGetApplicationAgreementsQuery,
  useLazyGetApplicationChatQuery,
  useLazyGetCofNewApplicationDocumentsQuery,
  useLazyGetNewApplicationInfoQuery,
  useLazyGetNewApplicationOrganizationQuery,
  useLazyGetNewApplicationsValidatorsQuery,
  useUpdateCOFQuestionnarieMutation,
  useUpdateCompanyOfficialApplicationMutation,
  useUpdateCompanyOfficialStatusMutation,
} from '../../store/services';
import {
  checkChatMessages,
  saveChanges,
  setAllowedDocumentsCategories,
  updateStepsStatus,
} from '../store/slices';
import getCofandIndividualQuestionnarieBody from '../../individual/helpers/get-qestionnarie-body';

const useApplication = ({
  validators,
  clientsActivities,
  applicationStepsStatus,
  agreements,
  ongoingMonitoring,
  ongoingCheckingStatus,
  newApplicationInfo,
  questionnarieFields,
  crsAndFatcaValues,
  haveSecondPassport,
  documents,
  affilatedCompaniesValues,
  applicationId,
  activePermissions,
  questionnarieGetValues,
  questionnarieSetError,
  onSaveCRSAndFatca,
  onSaveAffilatedCompanies,
  prevApplicationId,
  questionnarieSetValue,
  defaultQuestionnarieFormValues,
}: {
  validators?: NewApplicationsValidatorResponse;
  clientsActivities?: SelectItemProps[];
  applicationStepsStatus?: NewApplicationIndividualStepsStatuses;
  agreements?: ApplicationAgreement[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  newApplicationInfo?: NewApplicationListItem;
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues;
  crsAndFatcaValues?: CRSAndFatcaFormValues[];
  haveSecondPassport?: boolean;
  documents?: ApplicationDocument[];
  affilatedCompaniesValues?: AffilatedCompanyFormValues[];
  applicationId?: string;
  activePermissions?: PermissionEnum[];
  questionnarieGetValues: UseFormGetValues<COFAndIndividualQuestionnarieFormValues>;
  questionnarieSetError: UseFormSetError<COFAndIndividualQuestionnarieFormValues>;
  onSaveCRSAndFatca: () => void;
  onSaveAffilatedCompanies: () => void;
  prevApplicationId?: string;
  questionnarieSetValue: UseFormSetValue<COFAndIndividualQuestionnarieFormValues>;
  defaultQuestionnarieFormValues?: COFAndIndividualQuestionnarieFormValues;
}) => {
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [statusChangeError, setStatusChangeError] = useState('');
  const [applicationLoadErrorVisible, setApplicationLoadErrorVisible] =
    useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [updateQuestionnarieError, setUpdateQuestionnarieError] = useState('');

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
    setError,
    clearErrors,
    setValue,
  } = useForm<FormValues>({
    resolver: ajvResolver(
      validators?.updateClientApplicationSchema as ValidatorsProps,
    ),
  });

  const fields = watch();

  const prevClientActivitiy = usePrevious(fields.clientActivityId);

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const stepsStatus = updateApplicationStepStatuses({
    values: fields,
    stepsStatus: applicationStepsStatus,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    applicationScheme: newApplicationInfo?.applicationScheme,
    questionnarieFields,
    crsAndFatcaValues,
    haveSecondPassport,
    documents,
    affilatedCompaniesValues,
    clientsActivities,
  });

  const prevStepsStatus = usePrevious(stepsStatus);

  const {
    riskScoreRanges,
    customersTransactionTypes,
    deliveryChannelsIndividual,
    productsIndividual,
    monthlyTurnover,
    numberOfIncomingTransactions,
    numberOfOutgoingTransactions,
    valueOfIncomingTransactions,
    valueOfOutgoingTransactions,
    maxValueOfIncomingPayment,
    maxValueOfOutgoingPayment,
  } = useAppSelector((state) => state.customersDictionaries);

  const [getDictionaries] = useLazyGetCustomersInfoDictionariesQuery();
  const [getApplicationChat] = useLazyGetApplicationChatQuery();
  const [getInfo, { isLoading, isFetching }] =
    useLazyGetNewApplicationInfoQuery({
      refetchOnReconnect: true,
      refetchOnFocus: true,
    });
  const [getApplicationOrganization] =
    useLazyGetNewApplicationOrganizationQuery();
  const [updateInfo, { isLoading: requestInProgress }] =
    useUpdateCompanyOfficialApplicationMutation();
  const [getValidators] = useLazyGetNewApplicationsValidatorsQuery();
  const [createCompanyOfficial, { isLoading: createinProgress }] =
    useUpdateCompanyOfficialStatusMutation();
  const [getCofApplicationDocuments] =
    useLazyGetCofNewApplicationDocumentsQuery();
  const [
    getApplicationAgreements,
    { isLoading: agreementsIsLoading, isFetching: agreementsFetching },
  ] = useLazyGetApplicationAgreementsQuery();
  const [getCustomersEmployeesList] = useLazyGetCustomersEmployeesListQuery();

  const [updateCOFQuestionnarie, { isLoading: questionnarieUpdateInProgress }] =
    useUpdateCOFQuestionnarieMutation();

  const getApplicationDocuments = (id: string) => {
    getCofApplicationDocuments(id);
  };

  const onCreateCustomer = async () => {
    setStatusChangeError('');

    const response = (await createCompanyOfficial({
      id: applicationId as string,
      body: { status: NewApplicationStatus.ACCEPTED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    } else {
      setUserCreated(true);

      setTimeout(() => {
        getApplicationOrganization(applicationId as string);
      }, 8000);
    }
  };

  const getInfoAction = useCallback(async () => {
    if (applicationId) {
      const response = (await getInfo(applicationId)) as {
        error: ResponseError;
        data: any;
      };

      getApplicationChat(applicationId);

      if (activePermissions?.includes(PermissionEnum.USERS_LIST)) {
        getCustomersEmployeesList();
      }

      if (response) {
        if (response?.error?.data?.message) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0].property === 'coreError'
          ) {
            setApplicationLoadErrorVisible(true);
          }
        }
      }
    }
  }, [applicationId, activePermissions]);

  const onSubmit = handleSubmit(async (values) => {
    setUpdateError('');
    setUpdateQuestionnarieError('');
    dispatch(saveChanges(values));

    const body: {
      firstName?: string;
      lastName?: string;
      nationalityId?: string;
      email?: string;
      dob?: string;
      deliveryChannelId?: string;
      taxNumber?: string;
      clientActivityId?: string;
      nameOfEmployer?: string;
      employeePosition?: string;
      countryOfEmployerId?: string;
      typeOfActivityDescription?: string;
    } = {};

    if (
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
      ) &&
      newApplicationInfo?.basePersonalInfoEditable
    ) {
      if (values.firstName) {
        body.firstName = values.firstName;
      }

      if (values.lastName) {
        body.lastName = values.lastName;
      }

      if (values.email) {
        body.email = values.email;
      }

      if (values.dob) {
        const dateValue = new Date(values.dob);

        const dob = `${dateValue.getFullYear()}-${(dateValue.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${dateValue
          .getDate()
          .toString()
          .padStart(2, '0')}`;

        body.dob = dob;
      }
    }

    if (values.nationalityId) {
      body.nationalityId = values.nationalityId;
    }

    if (values.taxNumber) {
      body.taxNumber = values.taxNumber;
    }

    if (values.deliveryChannelId) {
      body.deliveryChannelId = values.deliveryChannelId;
    }

    if (values.clientActivityId) {
      body.clientActivityId = values.clientActivityId;
    }

    if (values.nameOfEmployer) {
      body.nameOfEmployer = values.nameOfEmployer;
    }

    if (values.employeePosition) {
      body.employeePosition = values.employeePosition;
    }

    if (values.countryOfEmployerId) {
      body.countryOfEmployerId = values.countryOfEmployerId;
    }

    if (values.typeOfActivityDescription) {
      body.typeOfActivityDescription = values.typeOfActivityDescription;
    }

    const response = (await updateInfo({
      body: {
        ...body,
        participatesInPolitics: Boolean(
          values.participatesInPolitics === 'yes',
        ),
        isUSResident: Boolean(values.isUSResident === 'yes'),
      },
      id: applicationId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError: setUpdateError,
      });
    }

    if (
      (newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme
            ?.businessAndEmploymentActive)) ||
      newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159'
    ) {
      const questionnarieValues = questionnarieGetValues();

      const questionnarieBody = getCofandIndividualQuestionnarieBody({
        questionnarieValues,
        scheme: newApplicationInfo?.applicationScheme,
      });

      const questionnarieResponse = (await updateCOFQuestionnarie({
        id: newApplicationInfo?.id,
        body: questionnarieBody,
      })) as { error?: ResponseError };

      if (questionnarieResponse?.error) {
        responseErrorsResolver({
          errors: questionnarieResponse?.error,
          setError: questionnarieSetError,
          setGlobalError: setUpdateQuestionnarieError,
        });
      }

      if (
        newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159'
      ) {
        onSaveCRSAndFatca();
        onSaveAffilatedCompanies();
      }
    }
  });

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      getInfoAction();
    }
  }, [applicationId, getInfoAction, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    if (applicationId) {
      if (
        !riskScoreRanges ||
        !customersTransactionTypes ||
        !deliveryChannelsIndividual ||
        !productsIndividual ||
        !clientsActivities ||
        !monthlyTurnover ||
        !numberOfIncomingTransactions ||
        !numberOfOutgoingTransactions ||
        !valueOfIncomingTransactions ||
        !valueOfOutgoingTransactions ||
        !maxValueOfIncomingPayment ||
        !maxValueOfOutgoingPayment
      ) {
        setTimeout(() => {
          getDictionaries();
        }, 10);
      }
    }
  }, [
    applicationId,
    riskScoreRanges,
    customersTransactionTypes,
    deliveryChannelsIndividual,
    productsIndividual,
    clientsActivities,
    monthlyTurnover,
    numberOfIncomingTransactions,
    numberOfOutgoingTransactions,
    valueOfIncomingTransactions,
    valueOfOutgoingTransactions,
    maxValueOfIncomingPayment,
    maxValueOfOutgoingPayment,
  ]);

  useEffect(() => {
    if (prevStepsStatus && !isEqual(stepsStatus, prevStepsStatus)) {
      dispatch(updateStepsStatus(stepsStatus));
    }
  }, [stepsStatus, prevStepsStatus, dispatch]);

  useEffect(() => {
    if (
      applicationId &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
      )
    ) {
      getCofApplicationDocuments(applicationId);
    }
  }, [applicationId]);

  useEffect(() => {
    if (!validators) {
      getValidators();
    }
  }, [getValidators, validators]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'ad811a02-87ed-4e19-ad45-770c0fd8fe41',
      )
    ) {
      const nextAllowedCategories =
        newApplicationInfo?.applicationScheme?.documentsCategories?.edges?.map(
          (item) => item.node,
        ) as AllowedCategory[];

      nextAllowedCategories?.unshift({
        id: 'identification-category',
        name: 'Identification documents',
        required: false,
      });

      dispatch(setAllowedDocumentsCategories(nextAllowedCategories));
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (
      prevClientActivitiy &&
      prevClientActivitiy === employmentActivity?.value &&
      fields.clientActivityId !== employmentActivity?.value
    ) {
      setValue('nameOfEmployer', '');
      setValue('countryOfEmployerId', '');
      setValue('employeePosition', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === businessOwnerActivity?.value &&
      fields.clientActivityId !== businessOwnerActivity?.value
    ) {
      setValue('countryOfEmployerId', '');
      questionnarieSetValue('nameOfCompany', '');
      questionnarieSetValue('ownership', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy ===
        clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value &&
      fields.clientActivityId !==
        clientsActivities?.find((item) => item.name === 'Self-employed')?.value
    ) {
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === otherActivity?.value &&
      fields.clientActivityId !== otherActivity?.value
    ) {
      setValue('typeOfActivityDescription', '');
    }
  }, [fields.clientActivityId, prevClientActivitiy]);

  useEffect(() => {
    if (newApplicationInfo) {
      const formValues = {
        firstName: newApplicationInfo.firstName || '',
        lastName: newApplicationInfo.lastName || '',
        phone: newApplicationInfo.phone || '',
        email: newApplicationInfo.email || '',
        dob: newApplicationInfo.dob || '',
        city: newApplicationInfo?.city || '',
        countryId: newApplicationInfo?.country?.id || '',
        postalCode: newApplicationInfo?.postalCode || '',
        address1: newApplicationInfo?.address1 || '',
        address2: newApplicationInfo?.address2 || '',
        passportNumber: newApplicationInfo.passportNumber || '',
        passportExpiredDate: newApplicationInfo.passportExpiredDate || '',
        participatesInPolitics: !newApplicationInfo.participatesInPolitics
          ? 'no'
          : 'yes',
        isUSResident: !newApplicationInfo.isUSResident ? 'no' : 'yes',
        nationalityId: newApplicationInfo.nationalityId || '',
        deliveryChannelId: newApplicationInfo.deliveryChannelId || '',
        userId: newApplicationInfo.userId || '',
        sumSubStatus: newApplicationInfo?.sumSubApplication?.reviewAnswer || '',
        taxNumber: newApplicationInfo?.taxNumber || '',
        birthCountryId: newApplicationInfo?.birthCountryId || '',
        placeOfBirth: newApplicationInfo?.placeOfBirth || '',
        correspondenceAddressSameAsRegisteredAddress:
          newApplicationInfo?.correspondenceAddressSameAsRegisteredAddress,
        correspondenceCountryId:
          newApplicationInfo?.correspondenceCountryId || '',
        correspondenceCity: newApplicationInfo?.correspondenceCity || '',
        correspondencePostalCode:
          newApplicationInfo?.correspondencePostalCode || '',
        correspondenceAddress1:
          newApplicationInfo?.correspondenceAddress1 || '',
        correspondenceAddress2:
          newApplicationInfo?.correspondenceAddress2 || '',
        clientActivityId: newApplicationInfo?.clientActivityId || '',
        nameOfEmployer: newApplicationInfo?.nameOfEmployer || '',
        employeePosition: newApplicationInfo?.employeePosition || '',
        countryOfEmployerId: newApplicationInfo?.countryOfEmployerId || '',
        typeOfActivityDescription:
          newApplicationInfo?.typeOfActivityDescription || '',
      };

      reset(formValues);
      setDefaultFormValues(formValues);
      getApplicationAgreements(newApplicationInfo.id);
    }
  }, [reset, dispatch, newApplicationInfo]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(checkChatMessages());
    }, 60000);
  }, [dispatch]);

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();

      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    if (
      !isEqual(fields, defaultFormValues) ||
      !isEqual(questionnarieFields, defaultQuestionnarieFormValues)
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    fields,
    defaultFormValues,
    questionnarieFields,
    defaultQuestionnarieFormValues,
  ]);

  useEffect(() => {
    let title = 'New Application';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  return {
    userCreated,
    isLoading,
    isFetching,
    applicationLoadErrorVisible,
    setStatusChangeError,
    getApplicationDocuments,
    onSubmit,
    requestInProgress,
    questionnarieUpdateInProgress,
    fields,
    defaultFormValues,
    statusChangeError,
    onCreateCustomer,
    createinProgress,
    setError,
    clearErrors,
    control,
    errors,
    setValue,
    isDirty,
    setDirtyPopupVisible,
    dirtyPopupVisible,
    updateError,
    updateQuestionnarieError,
    agreementsIsLoading,
    agreementsFetching,
  };
};

export default useApplication;
